import { PlusOutlined } from '@ant-design/icons';
import type { Category } from '@shop-monorepo/types';
import type { TableProps } from 'antd';
import { Button } from 'antd';
import { useMemo } from 'react';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { BooleanSelectFilter } from '../../components/BooleanSelectFilter';
import { DeleteButton } from '../../components/DeleteButton';
import { EditButton } from '../../components/EditButton';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  StyledTable,
  TableContainer,
  Toolbar,
} from '../../components/PageLayout';
import { RightAlignedSpace } from '../../components/RightAlignedSpace';
import { StatusIndicator } from '../../components/StatusIndicator';
import { useCategories, useDeleteCategory } from '../../hooks/useCategoryQueries';
import { useListNavigate } from '../../hooks/useListReturn';
import { useTableSorter } from '../../hooks/useTableSorter';
import { useBooleanParams } from '../../hooks/useUrlParam';
import { ROUTES } from '../../routes/routes';

const AddSubcategoryButton = styled(Button)`
  color: #ff8e8e !important;

  &:hover {
    color: #ff6b6b !important;
  }
`;

export const CategoriesPage: React.FC = () => {
  const goToDetail = useListNavigate();
  const { sorter, handleTableChange } = useTableSorter();
  const filters = useBooleanParams(['isActive']);
  const { data: categories, isLoading } = useCategories(sorter, filters);
  const { mutate: deleteCategory } = useDeleteCategory();

  const rootCategories = useMemo(() => categories || [], [categories]);

  const columns: TableProps<Category>['columns'] = [
    {
      title: 'Назва',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
    },
    {
      title: 'Статус',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 120,
      sorter: true,
      render: (isActive: boolean) => <StatusIndicator isActive={isActive} />,
    },
    {
      title: 'Дія',
      key: 'action',
      width: 180,
      align: 'right',
      render: (_, record) => (
        <RightAlignedSpace size="small">
          {record.parentId === null && (
            <AddSubcategoryButton
              type="text"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => goToDetail(`${ROUTES.CATEGORY_CREATE}?parentId=${record.id}`)}
            />
          )}
          <EditButton
            onClick={() =>
              goToDetail(generatePath(ROUTES.CATEGORY_EDIT, { id: String(record.id) }))
            }
          />
          <DeleteButton onConfirm={() => deleteCategory(record.id)} entityName={record.name} />
        </RightAlignedSpace>
      ),
    },
  ];

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Категорії</PageTitle>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => goToDetail(ROUTES.CATEGORY_CREATE)}
        >
          Додати категорію
        </Button>
      </PageHeader>
      <Toolbar>
        <BooleanSelectFilter name="isActive" placeholder="Активний" />
      </Toolbar>
      <TableContainer>
        <StyledTable
          columns={columns}
          dataSource={rootCategories}
          rowKey="id"
          loading={isLoading}
          pagination={false}
          expandable={{
            defaultExpandAllRows: true,
            indentSize: 24,
          }}
          onChange={handleTableChange}
          scroll={{ x: 500 }}
        />
      </TableContainer>
    </PageContainer>
  );
};
